import { Component } from '@theme/component';

const normalize = (value) => String(value ?? '').normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase('en').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
const unique = (values) => [...new Map(values.filter((v) => typeof v === 'string' && v.trim()).map((v) => [normalize(v), v.trim()])).values()].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
const parseList = (value) => { try { const parsed = JSON.parse(value || '[]'); return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : []; } catch { return []; } };
const matchingOption = (options, value) => options.find((option) => normalize(option) === normalize(value)) || '';
const changedEvent = 'carekee:directory:changed';

class CarekeeProviderList extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.dispatchEvent(new CustomEvent(changedEvent, { bubbles: true, detail: { group: this.dataset.directoryGroup } }));
  }

  updatedCallback() {
    super.updatedCallback();
    this.dispatchEvent(new CustomEvent(changedEvent, { bubbles: true, detail: { group: this.dataset.directoryGroup } }));
  }

  disconnectedCallback() {
    const group = this.dataset.directoryGroup;
    super.disconnectedCallback();
    document.dispatchEvent(new CustomEvent(changedEvent, { detail: { group } }));
  }
}

class CarekeeDirectoryControls extends Component {
  #abort;
  #frame;
  #records = [];
  #lists = [];
  #labels = {};
  #state;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    for (const list of this.#lists) {
      list.hidden = false;
      for (const card of list.querySelectorAll('[data-ckm-card]')) card.hidden = false;
      const section = list.closest('.carekee-module-section');
      if (section) section.hidden = false;
    }
    super.disconnectedCallback();
  }

  #initialize() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    this.#abort = new AbortController();
    this.#state ??= this.#readURL();
    try { this.#labels = JSON.parse(this.querySelector('[data-ckm-labels]')?.textContent || '{}'); } catch { this.#labels = {}; }
    const options = { signal: this.#abort.signal };
    this.addEventListener('input', (event) => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-ckm-search]')) return;
      this.#state.q = event.target.value;
      this.#state.page = 1;
      this.#apply();
    }, options);
    this.addEventListener('change', (event) => this.#change(event), options);
    this.addEventListener('click', (event) => this.#click(event), options);
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const list = event.target.closest('carekee-provider-list');
      if (list?.dataset.directoryGroup === this.dataset.directoryGroup && event.target.closest('[data-ckm-pagination]')) this.#click(event);
    }, options);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        const open = this.querySelector('details[open]');
        open?.removeAttribute('open');
        open?.querySelector('summary')?.focus();
      }
    }, options);
    this.addEventListener('submit', (event) => {
      if (event.target instanceof HTMLFormElement && event.target.matches('[data-ckm-form]')) { event.preventDefault(); this.#apply(); }
    }, options);
    document.addEventListener(changedEvent, (event) => {
      if (event.detail?.group === this.dataset.directoryGroup) this.#scheduleRefresh();
    }, options);
    document.addEventListener('DOMContentLoaded', () => this.#refresh(), options);
    document.addEventListener('shopify:section:load', () => this.#scheduleRefresh(), options);
    document.addEventListener('shopify:section:reorder', () => this.#scheduleRefresh(), options);
    document.addEventListener('shopify:section:select', (event) => this.#selectEditorRegion(event), options);
    document.addEventListener('shopify:block:select', (event) => this.#selectEditorRegion(event), options);
    window.addEventListener('popstate', () => { this.#state = this.#readURL(); this.#refresh(); }, options);
    this.#refresh();
    this.#scheduleRefresh();
    this.dataset.ready = 'true';
  }

  #scheduleRefresh() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => { if (this.isConnected) this.#refresh(); });
  }

  #readURL() {
    const p = new URL(window.location.href).searchParams;
    return {
      view: p.get('directory_view') === 'specialists' ? 'specialists' : 'hospitals',
      q: p.get('q') || '', city: p.get('city') || '', specialty: p.getAll('specialty'),
      treatment: p.get('treatment') || '', hospital_type: p.get('hospital_type') || '', service: p.get('service') || '',
      sort: ['az', 'za'].includes(p.get('directory_sort')) ? p.get('directory_sort') : 'featured',
      page: Math.max(1, Number.parseInt(p.get('listing_page') || '1', 10) || 1),
    };
  }

  #refresh() {
    const group = this.dataset.directoryGroup || 'main';
    this.#lists = [...document.querySelectorAll('carekee-provider-list')].filter((list) => list.dataset.directoryGroup === group);
    this.#records = this.#lists.flatMap((list) => [...list.querySelectorAll('[data-ckm-card]')].map((element, index) => {
      if (!element.dataset.featuredIndex) element.dataset.featuredIndex = String(index + 1);
      return {
        element, list, order: Number(element.dataset.featuredIndex), id: element.dataset.recordId,
        kind: element.dataset.kind, name: element.dataset.name || '', city: element.dataset.city || '',
        specialty: parseList(element.dataset.specialties), treatment: parseList(element.dataset.treatments),
        hospital_type: element.dataset.hospitalType || '', service: parseList(element.dataset.services),
        search: normalize(element.dataset.search),
      };
    }));
    this.#options();
    this.#apply(false);
  }

  #select(key, values, label) {
    const select = this.querySelector(`[data-ckm-filter="${key}"]`);
    const available = unique(values);
    this.#state[key] = matchingOption(available, this.#state[key]);
    if (!(select instanceof HTMLSelectElement)) return;
    select.replaceChildren(new Option(label || '', ''), ...available.map((value) => new Option(value, value)));
    select.value = this.#state[key];
    select.disabled = available.length === 0;
  }

  #options() {
    const pool = this.#records.filter((r) => r.kind === this.#state.view);
    this.#select('city', pool.map((r) => r.city), this.#labels.all_cities);
    const cityPool = pool.filter((r) => !this.#state.city || normalize(r.city) === normalize(this.#state.city));
    const specialties = unique(cityPool.flatMap((r) => r.specialty));
    this.#state.specialty = unique(this.#state.specialty.map((value) => matchingOption(specialties, value)).filter(Boolean));
    const holder = this.querySelector('[data-ckm-specialty-options]');
    const activeValue = document.activeElement?.matches('[data-ckm-specialty]') ? document.activeElement.value : null;
    if (holder) {
      holder.replaceChildren(...specialties.map((value) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        const span = document.createElement('span');
        input.type = 'checkbox'; input.value = value; input.dataset.ckmSpecialty = '';
        input.checked = this.#state.specialty.includes(value);
        span.textContent = value; label.append(input, span); return label;
      }));
      if (activeValue) [...holder.querySelectorAll('input')].find((input) => input.value === activeValue)?.focus();
    }
    const selection = this.querySelector('[data-ckm-specialty-value]');
    if (selection) selection.textContent = this.#state.specialty.length === 0 ? this.#labels.all_specialties : this.#state.specialty.length === 1 ? this.#state.specialty[0] : (this.#labels.selected || '__COUNT__').replace('__COUNT__', String(this.#state.specialty.length));
    const clinicalPool = cityPool.filter((r) => !this.#state.specialty.length || r.specialty.some((s) => this.#state.specialty.includes(s)));
    this.#select('treatment', clinicalPool.flatMap((r) => r.treatment), this.#labels.all_treatments);
    this.#select('hospital_type', pool.map((r) => r.hospital_type), this.#labels.all_types);
    this.#select('service', pool.flatMap((r) => r.service), this.#labels.all_services);
    const search = this.querySelector('[data-ckm-search]');
    if (search && search.value !== this.#state.q) search.value = this.#state.q;
    const sort = this.querySelector('[data-ckm-sort]');
    if (sort) sort.value = this.#state.sort;
  }

  #matches(r) {
    const s = this.#state;
    return r.kind === s.view
      && (!s.city || normalize(r.city) === normalize(s.city))
      && (!s.specialty.length || r.specialty.some((v) => s.specialty.includes(v)))
      && (!s.treatment || r.treatment.some((v) => normalize(v) === normalize(s.treatment)))
      && (!s.hospital_type || normalize(r.hospital_type) === normalize(s.hospital_type))
      && (!s.service || r.service.some((v) => normalize(v) === normalize(s.service)))
      && normalize(s.q).split(' ').filter(Boolean).every((word) => r.search.includes(word));
  }

  #sorted(records) {
    return [...records].sort((a, b) => this.#state.sort === 'featured' ? a.order - b.order : a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) * (this.#state.sort === 'za' ? -1 : 1));
  }

  #apply(writeURL = true) {
    const s = this.#state;
    const matches = this.#sorted(this.#records.filter((r) => this.#matches(r)));
    const activeList = this.#lists.find((list) => list.dataset.kind === s.view);
    const size = Math.max(1, Number.parseInt(activeList?.dataset.pageSize || '12', 10));
    const pages = Math.max(1, Math.ceil(matches.length / size));
    s.page = Math.min(s.page, pages);
    const offset = (s.page - 1) * size;
    const visible = new Set(matches.slice(offset, offset + size).map((r) => r.element));
    for (const record of this.#records) record.element.hidden = !visible.has(record.element);
    for (const list of this.#lists) {
      const isActive = list.dataset.kind === s.view;
      list.hidden = !isActive && !this.dataset.designMode;
      const section = list.closest('.carekee-module-section');
      if (section) section.hidden = list.hidden;
      const grid = list.querySelector('[data-ckm-grid]');
      const cards = this.#sorted(this.#records.filter((r) => r.list === list));
      const current = [...(grid?.querySelectorAll(':scope > [data-ckm-card]') || [])];
      if (grid && !this.dataset.designMode && cards.some((r, i) => r.element !== current[i])) {
        const fragment = document.createDocumentFragment();
        for (const record of cards) fragment.append(record.element);
        grid.append(fragment);
      }
      for (const slot of list.querySelectorAll('[data-ckm-slot]')) slot.hidden = false;
    }
    for (const kind of ['hospitals', 'specialists']) {
      const count = this.#records.filter((r) => r.kind === kind).length;
      const counter = this.querySelector(`[data-ckm-count="${kind}"]`);
      if (counter) counter.textContent = String(count);
      const button = this.querySelector(`[data-ckm-view="${kind}"]`);
      button?.setAttribute('aria-pressed', String(kind === s.view));
    }
    const status = this.querySelector('[data-ckm-status]');
    if (status) status.textContent = (this.#labels.results || '').replace('__COUNT__', String(matches.length)).replace('__START__', String(matches.length ? offset + 1 : 0)).replace('__END__', String(Math.min(offset + size, matches.length)));
    const noMatches = this.querySelector('[data-ckm-no-matches]');
    if (noMatches) noMatches.hidden = matches.length > 0 || this.#records.filter((r) => r.kind === s.view).length === 0;
    for (const list of this.#lists) {
      const pagination = list.querySelector('[data-ckm-pagination]');
      if (!pagination) continue;
      pagination.hidden = list !== activeList || pages <= 1;
      const previous = pagination.querySelector('[data-ckm-prev]');
      const next = pagination.querySelector('[data-ckm-next]');
      if (previous) previous.disabled = s.page <= 1;
      if (next) next.disabled = s.page >= pages;
      const label = pagination.querySelector('[data-ckm-page-label]');
      if (label) label.textContent = (this.#labels.page || '').replace('__PAGE__', String(s.page)).replace('__PAGES__', String(pages));
    }
    this.#chips();
    if (writeURL) this.#writeURL();
  }

  #chips() {
    const holder = this.querySelector('[data-ckm-chips]');
    if (!holder) return;
    const values = [];
    if (this.#state.q.trim()) values.push(['q', this.#state.q.trim()]);
    for (const key of ['city', 'treatment', 'hospital_type', 'service']) if (this.#state[key]) values.push([key, this.#state[key]]);
    for (const value of this.#state.specialty) values.push(['specialty', value]);
    holder.hidden = values.length === 0;
    holder.replaceChildren(...values.map(([key, value]) => {
      const button = document.createElement('button');
      button.type = 'button'; button.dataset.ckmRemove = key; button.dataset.value = value;
      button.setAttribute('aria-label', `${this.#labels.remove}: ${value}`);
      const label = document.createElement('span'); label.textContent = value;
      const close = document.createElement('span'); close.textContent = '×'; close.setAttribute('aria-hidden', 'true');
      button.append(label, close); return button;
    }));
    if (values.length) {
      const clear = document.createElement('button'); clear.type = 'button'; clear.dataset.ckmClear = ''; clear.textContent = this.#labels.clear; holder.append(clear);
    }
  }

  #change(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-ckm-filter]')) this.#state[target.dataset.ckmFilter] = target.value;
    else if (target.matches('[data-ckm-specialty]')) this.#state.specialty = [...this.querySelectorAll('[data-ckm-specialty]:checked')].map((input) => input.value);
    else if (target.matches('[data-ckm-sort]')) this.#state.sort = target.value;
    else return;
    this.#state.page = 1;
    this.#options(); this.#apply();
  }

  #clear() {
    const { view, sort } = this.#state;
    this.#state = { view, sort, q: '', city: '', specialty: [], treatment: '', hospital_type: '', service: '', page: 1 };
  }

  #click(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!button) return;
    if (button.matches('[data-ckm-view]')) { this.#state.view = button.dataset.ckmView; this.#clear(); this.#options(); this.#apply(); }
    else if (button.matches('[data-ckm-clear]')) { this.#clear(); this.#options(); this.#apply(); }
    else if (button.matches('[data-ckm-remove]')) {
      const key = button.dataset.ckmRemove;
      if (key === 'specialty') this.#state.specialty = this.#state.specialty.filter((v) => v !== button.dataset.value);
      else this.#state[key] = '';
      this.#state.page = 1; this.#options(); this.#apply();
    } else if (button.matches('[data-ckm-prev],[data-ckm-next]')) {
      this.#state.page += button.matches('[data-ckm-next]') ? 1 : -1;
      this.#apply();
      const list = this.#lists.find((item) => item.dataset.kind === this.#state.view);
      list?.scrollIntoView({ block: 'start', behavior: 'auto' });
      list?.querySelector('h2')?.focus({ preventScroll: true });
    }
  }

  #selectEditorRegion(event) {
    if (!this.dataset.designMode || !(event.target instanceof Element)) return;
    const list = event.target.closest('carekee-provider-list') || event.target.querySelector('carekee-provider-list');
    if (!list || list.dataset.directoryGroup !== this.dataset.directoryGroup) return;
    this.#state.view = list.dataset.kind;
    this.#clear(); this.#options();
    const selectedCard = event.target.closest('[data-ckm-card]') || (event.detail?.blockId ? list.querySelector(`[data-block-id="${CSS.escape(event.detail.blockId)}"]`) : null);
    if (selectedCard) {
      const records = this.#sorted(this.#records.filter((r) => r.kind === this.#state.view));
      const index = records.findIndex((r) => r.element === selectedCard);
      const size = Number.parseInt(list.dataset.pageSize || '12', 10);
      if (index >= 0) this.#state.page = Math.floor(index / size) + 1;
    }
    this.#apply(false);
  }

  #writeURL() {
    if (this.dataset.designMode) return;
    const url = new URL(window.location.href);
    for (const key of ['q', 'city', 'specialty', 'treatment', 'hospital_type', 'service', 'directory_view', 'directory_sort', 'listing_page']) url.searchParams.delete(key);
    if (this.#state.q.trim()) url.searchParams.set('q', this.#state.q.trim());
    for (const key of ['city', 'treatment', 'hospital_type', 'service']) if (this.#state[key]) url.searchParams.set(key, normalize(this.#state[key]).replaceAll(' ', '-'));
    for (const value of this.#state.specialty) url.searchParams.append('specialty', normalize(value).replaceAll(' ', '-'));
    if (this.#state.view !== 'hospitals') url.searchParams.set('directory_view', this.#state.view);
    if (this.#state.sort !== 'featured') url.searchParams.set('directory_sort', this.#state.sort);
    if (this.#state.page > 1) url.searchParams.set('listing_page', String(this.#state.page));
    try { window.history.replaceState(window.history.state, '', url); } catch { return; }
  }
}

class CarekeeProfileNavigation extends Component {
  #abort;
  #frame;

  connectedCallback() { super.connectedCallback(); this.#initialize(); }
  updatedCallback() { super.updatedCallback(); this.#initialize(); }
  disconnectedCallback() { this.#abort?.abort(); cancelAnimationFrame(this.#frame); super.disconnectedCallback(); }

  #initialize() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const refresh = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        for (const link of this.querySelectorAll('a[href^="#"]')) {
          const id = link.getAttribute('href').slice(1);
          link.hidden = !document.getElementById(id);
        }
      });
    };
    for (const type of ['DOMContentLoaded', 'shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) document.addEventListener(type, refresh, { signal: this.#abort.signal });
    refresh();
  }
}

if (!customElements.get('carekee-provider-list')) customElements.define('carekee-provider-list', CarekeeProviderList);
if (!customElements.get('carekee-directory-controls')) customElements.define('carekee-directory-controls', CarekeeDirectoryControls);
if (!customElements.get('carekee-profile-navigation')) customElements.define('carekee-profile-navigation', CarekeeProfileNavigation);
